import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  TextField,
  Grid,
  Paper,
  Alert,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  Divider,
  IconButton,
  List,
  ListItemText,
  LinearProgress,
  Avatar,
  MenuItem
} from '@mui/material';
import {
  Add,
  SupportAgent,
  Send,
  Close,
  Reply,
  Visibility,
  Refresh,
  Schedule
} from '@mui/icons-material';
import { getMerchantTickets, getTicketDetails, createTicket, replyToTicket } from '../../services/api';
import { showLoadingToast, updateToast } from '../../utils/toast';

const Support = () => {
  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showNew, setShowNew] = useState(false);
  const [selected, setSelected] = useState(null);
  const [form, setForm] = useState({ subject: '', description: '', category: '' });
  const [reply, setReply] = useState('');
  const [page, setPage] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [error, setError] = useState(null);

  useEffect(() => { loadTickets(); }, []);

  const loadTickets = async () => {
    try {
      setLoading(true);
      const response = await getMerchantTickets(page, 20);
      setTickets(response.data || []);
      setTotalPages(Math.ceil((response.data?.length || 0) / 20));
    } catch (error) {
      console.error('Failed to load tickets:', error);
      updateToast('', 'Failed to load support tickets', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    if (!form.subject.trim() || !form.description.trim()) {
      updateToast('', 'Please fill in all required fields', 'error');
      return;
    }

    const toastId = showLoadingToast('Creating support ticket...');

    try {
      const response = await createTicket(form);
      
      updateToast(toastId, 'Support ticket created successfully!', 'success');
      setShowNew(false);
      setForm({ subject: '', description: '', category: '' }); // Reset form
      await loadTickets();
    } catch (error) {
      console.error('Failed to create ticket:', error);
      updateToast(toastId, 'Failed to create support ticket', 'error');
    }
  };

  const resetForm = () => {
    setForm({ subject: '', description: '', category: '' });
  };

  const viewTicket = async (ticket) => {
    try {
      const response = await getTicketDetails(ticket.ticketId);
      setSelected(response.data);
    } catch (error) {
      console.error('Failed to load ticket details:', error);
      updateToast('', 'Failed to load ticket details', 'error');
    }
  };

  const handleReply = async () => {
    if (!reply.trim()) {
      updateToast('', 'Please enter a reply message', 'error');
      return;
    }

    const toastId = showLoadingToast('Sending reply...');

    try {
      const response = await replyToTicket(selected.ticketId, { message: reply });
      
      // Clear reply field immediately
      setReply('');
      
      // Refresh ticket details to show the new reply
      await viewTicket(selected);
      
      updateToast(toastId, 'Reply sent successfully!', 'success');
    } catch (error) {
      updateToast(toastId, 'Failed to send reply', 'error');
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'OPEN':
        return { color: 'error', label: 'Open' };
      case 'IN_PROGRESS':
        return { color: 'warning', label: 'In Progress' };
      case 'RESOLVED':
        return { color: 'success', label: 'Resolved' };
      case 'CLOSED':
        return { color: 'default', label: 'Closed' };
      default:
        return { color: 'default', label: status };
    }
  };

  const getPriorityColor = (priority) => {
    switch (priority) {
      case 'HIGH':
        return 'error';
      case 'MEDIUM':
        return 'warning';
      case 'LOW':
        return 'success';
      default:
        return 'default';
    }
  };

  if (loading) {
    return (
      <Box sx={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center', 
        minHeight: '100vh',
        backgroundColor: '#f5f7fa'
      }}>
        <Box sx={{ textAlign: 'center' }}>
          <LinearProgress sx={{ width: '80%', mb: 2 }} />
          <Typography variant="body1" color="text.secondary">
            Loading support tickets...
          </Typography>
        </Box>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3, backgroundColor: "#f5f7fa", minHeight: "100vh" }}>
      {/* Header */}
      <Box sx={{ mb: 4, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <Typography variant="h4" sx={{ fontWeight: 600, color: "#1a1a1a" }}>
          Support Tickets
        </Typography>
        <Box sx={{ display: "flex", gap: 2 }}>
          <Button
            variant="contained"
            startIcon={<Add />}
            onClick={() => {
              resetForm();
              setShowNew(true);
            }}
            sx={{ textTransform: "none", fontWeight: 600 }}
          >
            Create Ticket
          </Button>
          <IconButton
            onClick={() => loadTickets()}
            sx={{ bgcolor: "white", border: "1px solid", borderColor: "divider" }}
          >
            <Refresh />
          </IconButton>
        </Box>
      </Box>

      {/* Error Alert */}
      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* Tickets List */}
      {tickets.length === 0 ? (
        <Card sx={{ textAlign: 'center', py: 8 }}>
          <CardContent>
            <SupportAgent sx={{ fontSize: 48, color: '#cbd5e1', mb: 2 }} />
            <Typography variant="h6" color="text.secondary" sx={{ mb: 2 }}>
              No support tickets found
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Create your first support ticket to get started
            </Typography>
          </CardContent>
        </Card>
      ) : (
        <Grid container spacing={3}>
          {tickets.map((ticket) => (
            <Grid item xs={12} key={ticket.id}>
              <Card 
                sx={{ 
                  cursor: 'pointer',
                  transition: 'all 0.2s',
                  '&:hover': {
                    transform: 'translateY(-2px)',
                    boxShadow: '0 4px 12px rgba(0,0,0,0.1)'
                  }
                }}
                onClick={() => viewTicket(ticket)}
              >
                <CardContent sx={{ p: 3 }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                    <Box sx={{ flex: 1 }}>
                      <Typography variant="h6" sx={{ fontWeight: 600, mb: 1, color: '#1a1a1a' }}>
                        {ticket.subject}
                      </Typography>
                      <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                        Ticket ID: {ticket.ticketId}
                      </Typography>
                      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                        {ticket.description}
                      </Typography>
                    </Box>
                    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 1 }}>
                      <Chip
                        icon={<Schedule />}
                        label={getStatusColor(ticket.status).label}
                        color={getStatusColor(ticket.status).color}
                        size="small"
                        sx={{ fontWeight: 600 }}
                      />
                      <Chip
                        label={ticket.priority}
                        color={getPriorityColor(ticket.priority)}
                        size="small"
                        sx={{ fontWeight: 600 }}
                      />
                    </Box>
                  </Box>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Typography variant="caption" color="text.secondary">
                      Created: {new Date(ticket.createdAt).toLocaleDateString()}
                    </Typography>
                    {ticket.closedAt && (
                      <Typography variant="caption" color="text.secondary">
                        Closed: {new Date(ticket.closedAt).toLocaleDateString()}
                      </Typography>
                    )}
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      )}

      {/* Pagination */}
      {totalPages > 1 && (
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}>
          <Button
            disabled={page === 0}
            onClick={() => setPage(page - 1)}
            sx={{ mr: 1 }}
          >
            Previous
          </Button>
          <Typography variant="body2" color="text.secondary">
            Page {page + 1} of {totalPages}
          </Typography>
          <Button
            disabled={page === totalPages - 1}
            onClick={() => setPage(page + 1)}
            sx={{ ml: 1 }}
          >
            Next
          </Button>
        </Box>
      )}

      {/* New Ticket Dialog */}
      <Dialog open={showNew} onClose={() => {
        setShowNew(false);
        resetForm();
      }} maxWidth="sm" fullWidth>
        <DialogTitle>Create Support Ticket</DialogTitle>
        <DialogContent>
          <Box component="form" onSubmit={handleCreate} sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
            <TextField
              label="Subject"
              value={form.subject}
              onChange={(e) => setForm({ ...form, subject: e.target.value })}
              fullWidth
              required
              sx={{ mb: 2 }}
            />
            <TextField
  label="Category"
  value={form.category}
  onChange={(e) => setForm({ ...form, category: e.target.value })}
  fullWidth
  required
  select
  sx={{ mb: 2 }}
>
  <MenuItem value="">
    Select category
  </MenuItem>
  <MenuItem value="TECHNICAL">
    Technical Issue
  </MenuItem>
  <MenuItem value="BILLING">
    Billing Question
  </MenuItem>
  <MenuItem value="ACCOUNT">
    Account Help
  </MenuItem>
  <MenuItem value="FEATURE">
    Feature Request
  </MenuItem>
  <MenuItem value="OTHER">
    Other
  </MenuItem>
</TextField>

            <TextField
              label="Description"
              value={form.description}
              onChange={(e) => setForm({ ...form, description: e.target.value })}
              fullWidth
              required
              multiline
              rows={4}
              sx={{ mb: 3 }}
            />
            <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
              <Button onClick={() => setShowNew(false)} sx={{ mr: 1 }}>
                Cancel
              </Button>
              <Button type="submit" variant="contained">
                Create Ticket
              </Button>
            </Box>
          </Box>
        </DialogContent>
      </Dialog>

      {/* Ticket Details Dialog */}
      <Dialog 
        open={!!selected} 
        onClose={() => setSelected(null)} 
        maxWidth="md" 
        fullWidth
        PaperProps={{ sx: { maxHeight: '85vh', overflow: 'hidden' } }}
      >
        <DialogTitle sx={{ pb: 2 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 2 }}>
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography variant="h6" sx={{ fontWeight: 600, mb: 1, wordBreak: 'break-word' }}>
                {selected?.subject}
              </Typography>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                Ticket ID: {selected?.ticketId}
              </Typography>
            </Box>
            <IconButton onClick={() => setSelected(null)} sx={{ flexShrink: 0 }}>
              <Close />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent sx={{ p: 3, overflow: 'auto', maxHeight: 'calc(85vh - 120px)' }}>
          <Box sx={{ mb: 3 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, flexWrap: 'wrap', gap: 2 }}>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                <Typography variant="body2" color="text.secondary">
                  Created: {selected && new Date(selected.createdAt).toLocaleDateString()}
                </Typography>
                {selected?.closedAt && (
                  <Typography variant="body2" color="text.secondary">
                    Closed: {new Date(selected.closedAt).toLocaleDateString()}
                  </Typography>
                )}
              </Box>
              <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', justifyContent: 'flex-end' }}>
                <Chip
                  icon={<Schedule />}
                  label={getStatusColor(selected?.status).label}
                  color={getStatusColor(selected?.status).color}
                  size="small"
                />
                <Chip
                  label={selected?.priority}
                  color={getPriorityColor(selected?.priority)}
                  size="small"
                />
              </Box>
            </Box>
          </Box>

          <Box sx={{ mb: 3, p: 2, bgcolor: 'grey.50', borderRadius: 2 }}>
            <Typography variant="body1" sx={{ mb: 1, fontWeight: 500 }}>
              Description
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {selected?.description}
            </Typography>
          </Box>

          {/* Replies */}
          {selected?.replies && selected.replies.length > 0 && (
            <Box sx={{ mb: 3 }}>
              <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
                Replies ({selected.replies.length})
              </Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                {selected.replies.map((reply) => (
                  <Box key={reply.id}>
                    <Paper sx={{ p: 2, bgcolor: reply.repliedByType === 'ADMIN' ? 'blue.50' : 'grey.50' }}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                        <Box sx={{ flex: 1 }}>
                          <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>
                            {reply.repliedByName}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {reply.repliedByType === 'ADMIN' ? 'Admin' : 'Support Agent'}
                          </Typography>
                          <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                            {new Date(reply.createdAt).toLocaleString()}
                          </Typography>
                        </Box>
                        <Avatar sx={{ bgcolor: reply.repliedByType === 'ADMIN' ? 'primary.main' : 'secondary.main', ml: 2 }}>
                          <SupportAgent />
                        </Avatar>
                      </Box>
                      <Typography variant="body2" sx={{ lineHeight: 1.5 }}>
                        {reply.message}
                      </Typography>
                    </Paper>
                  </Box>
                ))}
              </Box>
            </Box>
          )}

          {/* Reply Form */}
          <Box sx={{ mt: 3, p: 3, bgcolor: 'grey.100', borderRadius: 2, border: '1px solid', borderColor: 'divider' }}>
            <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
              Reply to Ticket
            </Typography>
            <Box component="form" onSubmit={handleReply} sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              <TextField
                label="Your Reply"
                value={reply}
                onChange={(e) => setReply(e.target.value)}
                fullWidth
                multiline
                rows={3}
                placeholder="Type your reply here..."
                variant="outlined"
                sx={{ mb: 1 }}
              />
              <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2, mt: 2 }}>
                <Button 
                  onClick={() => setReply('')} 
                  variant="outlined"
                  sx={{ minWidth: '80px' }}
                >
                  Cancel
                </Button>
                <Button 
                  type="submit" 
                  variant="contained" 
                  startIcon={<Send />}
                  sx={{ minWidth: '120px' }}
                >
                  Send Reply
                </Button>
              </Box>
            </Box>
          </Box>
        </DialogContent>
      </Dialog>
    </Box>
  );
};

export default Support;
